import React, { useState, Component } from 'react';
import { WebLayout } from '../components/WebLayout';
import { FocusTimer } from '../components/FocusTimer';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { ConfirmModal } from '../components/ConfirmModal';
import { Shield, Globe, MessageSquare, Youtube, Award, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUser } from '../contexts/UserContext';
interface DistractionOption {
  id: string;
  label: string;
  icon: ComponentType<{
    className?: string;
  }>;
  description: string;
  sites: string[];
}
const distractionOptions: DistractionOption[] = [{
  id: 'social',
  label: 'Social Media',
  icon: MessageSquare,
  description: 'Block Facebook, Instagram, Twitter, TikTok',
  sites: ['facebook.com', 'instagram.com', 'twitter.com', 'tiktok.com', 'reddit.com']
}, {
  id: 'video',
  label: 'Video Platforms',
  icon: Youtube,
  description: 'Block YouTube, Netflix, Twitch',
  sites: ['youtube.com', 'netflix.com', 'twitch.tv', 'hulu.com']
}, {
  id: 'news',
  label: 'News Sites',
  icon: Globe,
  description: 'Block news and media websites',
  sites: ['cnn.com', 'bbc.com', 'nytimes.com', 'reddit.com/r/news']
}, {
  id: 'shopping',
  label: 'Shopping',
  icon: Globe,
  description: 'Block e-commerce sites',
  sites: ['amazon.com', 'ebay.com', 'etsy.com', 'alibaba.com']
}];
export function FocusModePage() {
  const {
    completeFocusSession,
    stats
  } = useUser();
  const [focusActive, setFocusActive] = useState(false);
  const [selectedDistractions, setSelectedDistractions] = useState<string[]>([]);
  const [showBlockingOptions, setShowBlockingOptions] = useState(false);
  const [duration, setDuration] = useState(25);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showEndEarlyModal, setShowEndEarlyModal] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const toggleDistraction = (id: string) => {
    setSelectedDistractions(prev => prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]);
  };
  const startFocusMode = () => {
    if (selectedDistractions.length === 0) {
      setShowValidationModal(true);
      return;
    }
    setFocusActive(true);
    setShowBlockingOptions(false);
  };
  const handleFocusComplete = async () => {
    setFocusActive(false);
    setShowCompletionModal(true);
    await completeFocusSession(duration);
  };
  const handleEndEarly = () => {
    setFocusActive(false);
    setShowEndEarlyModal(false);
  };
  const getBlockedSites = () => {
    return selectedDistractions.flatMap(id => {
      const option = distractionOptions.find(o => o.id === id);
      return option?.sites || [];
    });
  };
  return <WebLayout data-id="element-732">
      {/* Desktop Header */}
      <header className="hidden lg:block px-8 pt-8 pb-6 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-[#252936]" data-id="element-733">
        <div className="max-w-7xl mx-auto" data-id="element-734">
          <div className="flex items-center justify-between" data-id="element-735">
            <div data-id="element-736">
              <h1 className="text-3xl font-fredoka font-bold text-game-dark dark:text-white mb-2" data-id="element-737">
                Focus Mode
              </h1>
              <p className="text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-738">
                Block distractions and enter your flow state
              </p>
            </div>
            {stats.focusSessions > 0 && <div className="flex items-center gap-2 bg-game-bg-purple px-4 py-2 rounded-full" data-id="element-739">
                <Award className="w-5 h-5 text-game-purple" data-id="element-740" />
                <span className="font-fredoka font-bold text-game-purple" data-id="element-741">
                  {stats.focusSessions} sessions completed
                </span>
              </div>}
          </div>
        </div>
      </header>

      {/* Mobile Header */}
      <header className="lg:hidden px-6 pt-6 pb-4" data-id="element-742">
        <div className="flex justify-between items-start" data-id="element-743">
          <div data-id="element-744">
            <h1 className="text-3xl font-fredoka font-bold text-game-dark dark:text-white mb-2" data-id="element-745">
              Focus Mode
            </h1>
            <p className="text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-746">
              Block distractions and get in the zone.
            </p>
          </div>
          {stats.focusSessions > 0 && <div className="flex items-center gap-2 bg-game-bg-purple px-3 py-2 rounded-full" data-id="element-747">
              <Award className="w-4 h-4 text-game-purple" data-id="element-748" />
              <span className="font-fredoka font-bold text-game-purple text-sm" data-id="element-749">
                {stats.focusSessions}
              </span>
            </div>}
        </div>
      </header>

      <main className="px-6 lg:px-8 py-6 lg:py-8" data-id="element-750">
        <div className="max-w-4xl mx-auto space-y-6" data-id="element-751">
          {!focusActive ? <>
              <Card className="p-6 lg:p-8" data-id="element-752">
                <div className="text-center mb-6 lg:mb-8" data-id="element-753">
                  <div className="w-20 h-20 lg:w-24 lg:h-24 bg-gradient-to-br from-game-purple to-game-teal rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-754">
                    <Shield className="w-10 h-10 lg:w-12 lg:h-12 text-white" data-id="element-755" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-fredoka font-bold text-game-dark dark:text-white mb-2" data-id="element-756">
                    Ready to Focus?
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 font-quicksand" data-id="element-757">
                    Select what to block and start your session
                  </p>
                </div>

                <Button onClick={() => setShowBlockingOptions(!showBlockingOptions)} variant="secondary" className="w-full mb-4" data-id="element-758">
                  <Shield className="w-5 h-5 mr-2" data-id="element-759" />
                  Configure Blocking ({selectedDistractions.length} selected)
                </Button>

                <AnimatePresence data-id="element-760">
                  {showBlockingOptions && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="space-y-3 mb-6" data-id="element-761">
                      <h3 className="font-fredoka font-bold text-game-dark dark:text-white mb-3" data-id="element-762">
                        What to block:
                      </h3>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-id="element-763">
                        {distractionOptions.map(option => {
                    const Icon = option.icon;
                    const isSelected = selectedDistractions.includes(option.id);
                    return <motion.button key={option.id} whileTap={{
                      scale: 0.98
                    }} onClick={() => toggleDistraction(option.id)} className={`w-full p-4 rounded-2xl border-2 transition-all text-left ${isSelected ? 'border-game-purple bg-game-bg-purple' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-[#252936]'}`} data-id="element-764">
                              <div className="flex items-start gap-3" data-id="element-765">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-game-purple' : 'bg-gray-100 dark:bg-gray-700'}`} data-id="element-766">
                                  <Icon className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-gray-600 dark:text-gray-400'}`} data-id="element-767" />
                                </div>
                                <div className="flex-1" data-id="element-768">
                                  <p className="font-fredoka font-bold text-game-dark dark:text-white" data-id="element-769">
                                    {option.label}
                                  </p>
                                  <p className="text-sm text-gray-600 dark:text-gray-400 font-quicksand" data-id="element-770">
                                    {option.description}
                                  </p>
                                  {isSelected && <div className="mt-2 flex flex-wrap gap-1" data-id="element-771">
                                      {option.sites.slice(0, 3).map(site => <span key={site} className="text-xs bg-white dark:bg-gray-800 px-2 py-1 rounded-full text-gray-600 dark:text-gray-400" data-id="element-772">
                                          {site}
                                        </span>)}
                                      {option.sites.length > 3 && <span className="text-xs text-gray-500 dark:text-gray-500 px-2 py-1" data-id="element-773">
                                          +{option.sites.length - 3} more
                                        </span>}
                                    </div>}
                                </div>
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-game-purple border-game-purple' : 'border-gray-300 dark:border-gray-600'}`} data-id="element-774">
                                  {isSelected && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-775">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-776" />
                                    </svg>}
                                </div>
                              </div>
                            </motion.button>;
                  })}
                      </div>
                    </motion.div>}
                </AnimatePresence>

                <Button onClick={startFocusMode} className="w-full" size="lg" disabled={selectedDistractions.length === 0} data-id="element-777">
                  Start Focus Session
                </Button>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-blue-200 dark:border-blue-800" data-id="element-778">
                <div className="flex items-start gap-3" data-id="element-779">
                  <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" data-id="element-780" />
                  <div data-id="element-781">
                    <h3 className="font-fredoka font-bold text-blue-900 dark:text-blue-100 mb-1" data-id="element-782">
                      How Blocking Works
                    </h3>
                    <p className="text-sm text-blue-800 dark:text-blue-200 font-quicksand leading-relaxed" data-id="element-783">
                      This is an <strong data-id="element-784">accountability tool</strong>. The sites
                      you select will be displayed during your focus session as
                      a reminder. For actual blocking, consider browser
                      extensions like <strong data-id="element-785">Freedom</strong> or{' '}
                      <strong data-id="element-786">Cold Turkey</strong>.
                    </p>
                    <p className="text-sm text-blue-800 dark:text-blue-200 font-quicksand leading-relaxed mt-2" data-id="element-787">
                      💡 <strong data-id="element-788">Tip:</strong> Earn{' '}
                      {Math.floor(duration / 5) * 10} XP by completing this{' '}
                      {duration}-minute session!
                    </p>
                  </div>
                </div>
              </Card>
            </> : <>
              <Card className="p-6 lg:p-8" data-id="element-789">
                <FocusTimer initialDuration={duration} onComplete={handleFocusComplete} onDurationChange={setDuration} data-id="element-790" />
              </Card>

              <Card className="p-6 bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border-red-200 dark:border-red-800" data-id="element-791">
                <div className="flex items-center gap-2 mb-4" data-id="element-792">
                  <Shield className="w-5 h-5 text-red-600 dark:text-red-400" data-id="element-793" />
                  <h3 className="font-fredoka font-bold text-red-900 dark:text-red-100" data-id="element-794">
                    Commitment: Avoid These Sites
                  </h3>
                </div>
                <div className="space-y-2" data-id="element-795">
                  {getBlockedSites().map(site => <div key={site} className="flex items-center gap-2 text-sm font-quicksand text-red-800 dark:text-red-200" data-id="element-796">
                      <div className="w-2 h-2 bg-red-500 rounded-full" data-id="element-797"></div>
                      <span className="font-mono" data-id="element-798">{site}</span>
                    </div>)}
                </div>
                <div className="mt-4 pt-4 border-t border-red-200 dark:border-red-800" data-id="element-799">
                  <p className="text-sm text-red-700 dark:text-red-300 font-quicksand" data-id="element-800">
                    💪 Stay strong! You've got this. Breaking focus will cost
                    you XP.
                  </p>
                </div>
              </Card>

              <Button onClick={() => setShowEndEarlyModal(true)} variant="secondary" className="w-full" data-id="element-801">
                End Session Early
              </Button>
            </>}
        </div>
      </main>

      {/* Modals */}
      <ConfirmModal isOpen={showValidationModal} onClose={() => setShowValidationModal(false)} onConfirm={() => setShowValidationModal(false)} title="Select Categories" message="Please select at least one category to block before starting your focus session." confirmText="Got it" cancelText="" variant="warning" data-id="element-802" />

      <ConfirmModal isOpen={showEndEarlyModal} onClose={() => setShowEndEarlyModal(false)} onConfirm={handleEndEarly} title="End Session Early?" message="Are you sure you want to end this session early? You won't earn any XP for incomplete sessions." confirmText="End Session" cancelText="Keep Going" variant="warning" data-id="element-803" />

      <AnimatePresence data-id="element-804">
        {showCompletionModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6" onClick={() => setShowCompletionModal(false)} data-id="element-805">
            <motion.div initial={{
          scale: 0.9,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.9,
          y: 20
        }} className="bg-white dark:bg-[#252936] rounded-3xl p-8 max-w-md w-full text-center" onClick={e => e.stopPropagation()} data-id="element-806">
              <div className="w-20 h-20 bg-gradient-to-br from-game-yellow to-game-coral rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-807">
                <Award className="w-10 h-10 text-white" data-id="element-808" />
              </div>
              <h2 className="text-3xl font-fredoka font-bold text-game-dark dark:text-white mb-2" data-id="element-809">
                Amazing Work! 🎉
              </h2>
              <p className="text-gray-600 dark:text-gray-400 font-quicksand mb-6" data-id="element-810">
                You completed a {duration}-minute focus session and earned{' '}
                <strong data-id="element-811">{Math.floor(duration / 5) * 10} XP</strong>!
              </p>
              <Button onClick={() => setShowCompletionModal(false)} className="w-full" data-id="element-812">
                Continue
              </Button>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </WebLayout>;
}